import React from 'react';
import { PackageMedia } from '@/services/api/packages';
import PhotoTileGrid from './PhotoTileGrid';

// Human labels for known POD kinds; unknown types humanize gracefully so new
// kinds render without a code change. Mirrors the packages detail drawer.
const MEDIA_TYPE_LABELS: Record<string, string> = {
  DELIVERY_PROOF: 'Foto Barang',
  DELIVERY_NOTE: 'Surat jalan',
  RETUR_ITEM: 'Foto Retur',
  RETUR_LABEL: 'Label Retur',
  RETUR_BA: 'BA Retur',
};
const MEDIA_TYPE_ORDER = Object.keys(MEDIA_TYPE_LABELS);

const humanize = (type: string) =>
  MEDIA_TYPE_LABELS[type] ??
  type
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

const groupMedia = (media: PackageMedia[]) => {
  const groups = new Map<string, PackageMedia[]>();
  for (const m of media) {
    if (!m?.url) continue;
    const bucket = groups.get(m.mediaType);
    if (bucket) bucket.push(m);
    else groups.set(m.mediaType, [m]);
  }
  return Array.from(groups.entries()).sort((a, b) => {
    const ia = MEDIA_TYPE_ORDER.indexOf(a[0]);
    const ib = MEDIA_TYPE_ORDER.indexOf(b[0]);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  });
};

// Compact per-item POD gallery for the shipment detail drawer. Renders nothing
// when the item has no proof photos (non-POD client, or before delivery).
const ItemPodGallery: React.FC<{ media?: PackageMedia[] }> = ({ media }) => {
  const groups = groupMedia(media ?? []);
  if (groups.length === 0) return null;

  return (
    <div className="mt-3 border-t border-stroke-soft-200 pt-2.5">
      <div className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.08em] text-text-soft-400">
        Proof of Delivery
      </div>
      <div className="space-y-2">
        {groups.map(([type, photos]) => (
          <div key={type}>
            <div className="mb-1 flex items-center gap-1.5">
              <span className="text-[11px] font-semibold text-text-sub-600">
                {humanize(type)}
              </span>
              {photos.length > 1 && (
                <span className="inline-flex items-center rounded-full bg-feature-lighter px-1.5 py-0.5 text-[9px] font-semibold text-feature-dark">
                  {photos.length} foto
                </span>
              )}
            </div>
            {/* Grouping above already drops media without a url, so an empty
                list here is impossible — no empty state needed. */}
            <PhotoTileGrid
              urls={photos.map((m) => m.url)}
              alt={humanize(type)}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default ItemPodGallery;
